// web-commands.ts — the PER-APP half of the web shape: maps oriora_skein's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. The one derived command (compute_signals) is the app's
// "paid draw" computed in signals.rs — on web it returns a minimal shell for now; a faithful web
// build reimplements that signal logic in JS (the known per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, Row, WebStore } from "./web-runtime";
import { computeSignalsWeb } from "./signals-web";
import type { Skein, Pattern, Project } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const SKEINS = "skeins";
const PATTERNS = "patterns";
const PROJECTS = "projects";

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function today(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}
// Non-negative finite float, mirrors db.rs::nn.
function nn(x: unknown): number {
  const n = Number(x);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}
function str(x: unknown): string {
  return typeof x === "string" ? x.trim() : x == null ? "" : String(x).trim();
}
// Canonical yarn-weight bucket, mirrors db.rs::norm_weight (unknowns pass through lowercased).
function normWeight(raw: unknown): string {
  const t = str(raw).toLowerCase();
  const map: Record<string, string> = {
    "0": "lace", lace: "lace", "lace weight": "lace", cobweb: "lace", thread: "lace",
    "1": "fingering", fingering: "fingering", sock: "fingering", "super fine": "fingering", "4ply": "fingering", "4 ply": "fingering",
    "2": "sport", sport: "sport", fine: "sport", "5ply": "sport",
    "3": "dk", dk: "dk", light: "dk", "8ply": "dk", "8 ply": "dk",
    "4": "worsted", worsted: "worsted", medium: "worsted", afghan: "worsted", "aran/worsted": "worsted", "10ply": "worsted",
    "5": "aran", aran: "aran", "12ply": "aran",
    "6": "bulky", bulky: "bulky", chunky: "bulky", craft: "bulky", rug: "bulky",
    "7": "super-bulky", "super bulky": "super-bulky", "super-bulky": "super-bulky", superbulky: "super-bulky", roving: "super-bulky",
    "8": "jumbo", jumbo: "jumbo",
  };
  return map[t] ?? t;
}
function normStatus(raw: unknown, fallback = "queued"): string {
  const s = str(raw).toLowerCase();
  return s === "queued" || s === "making" || s === "done" ? s : fallback;
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  const byCreatedDesc = (a: Row, b: Row) =>
    String((b as { created_at?: string }).created_at ?? "").localeCompare(
      String((a as { created_at?: string }).created_at ?? ""),
    );

  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Skeins ── (native ORDER BY created_at DESC)
    list_skeins: () => db.all(SKEINS).sort(byCreatedDesc),
    get_skein: ({ id }) => db.get(SKEINS, String(id)),
    create_skein: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const gps = nn(i.grams_per_skein);
      const count = (() => {
        const c = Number(i.skein_count ?? 1);
        return Number.isFinite(c) && c > 0 ? c : 1;
      })();
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        brand: str(i.brand),
        colorway: str(i.colorway),
        fiber: str(i.fiber),
        weight: normWeight(i.weight),
        yardage_per_skein: nn(i.yardage_per_skein),
        grams_per_skein: gps,
        skein_count: count,
        grams_remaining: i.grams_remaining == null ? nn(gps * count) : nn(i.grams_remaining),
        dye_lot: str(i.dye_lot),
        bought_where: str(i.bought_where),
        bought_on: str(i.bought_on),
        price: nn(i.price),
        currency: str(i.currency),
        notes: str(i.notes),
        photo: "",
        created_at: now,
        updated_at: now,
      };
      return db.insert(SKEINS, row);
    },
    update_skein: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const clean: Record<string, unknown> = { updated_at: nowIso() };
      if (p.brand !== undefined) clean.brand = str(p.brand);
      if (p.colorway !== undefined) clean.colorway = str(p.colorway);
      if (p.fiber !== undefined) clean.fiber = str(p.fiber);
      if (p.weight !== undefined) clean.weight = normWeight(p.weight);
      if (p.yardage_per_skein !== undefined) clean.yardage_per_skein = nn(p.yardage_per_skein);
      if (p.grams_per_skein !== undefined) clean.grams_per_skein = nn(p.grams_per_skein);
      if (p.skein_count !== undefined) {
        const c = Number(p.skein_count);
        clean.skein_count = Number.isFinite(c) && c > 0 ? c : undefined;
        if (clean.skein_count === undefined) delete clean.skein_count;
      }
      if (p.grams_remaining !== undefined) clean.grams_remaining = nn(p.grams_remaining);
      if (p.dye_lot !== undefined) clean.dye_lot = str(p.dye_lot);
      if (p.bought_where !== undefined) clean.bought_where = str(p.bought_where);
      if (p.bought_on !== undefined) clean.bought_on = str(p.bought_on);
      if (p.price !== undefined) clean.price = nn(p.price);
      if (p.currency !== undefined) clean.currency = str(p.currency);
      if (p.notes !== undefined) clean.notes = str(p.notes);
      db.update(SKEINS, String(id), clean);
      return null;
    },
    // Attach a photo to a skein — filed ON-DEVICE, never sent to the model (web mirror of the native command).
    set_skein_photo: ({ id, photo }) => {
      db.update(SKEINS, String(id), { photo: String(photo ?? ""), updated_at: nowIso() });
      return null;
    },
    delete_skein: ({ id }) => {
      db.remove(SKEINS, String(id));
      return null;
    },

    // ── Patterns ── (native ORDER BY created_at DESC)
    list_patterns: () => db.all(PATTERNS).sort(byCreatedDesc),
    get_pattern: ({ id }) => db.get(PATTERNS, String(id)),
    create_pattern: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        name: str(i.name),
        yarn_weight: normWeight(i.yarn_weight),
        total_yardage: nn(i.total_yardage),
        needle_size: str(i.needle_size),
        status: normStatus(i.status),
        source: str(i.source),
        notes: str(i.notes),
        created_at: now,
        updated_at: now,
      };
      return db.insert(PATTERNS, row);
    },
    update_pattern: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const clean: Record<string, unknown> = { updated_at: nowIso() };
      if (p.name !== undefined) clean.name = str(p.name);
      if (p.yarn_weight !== undefined) clean.yarn_weight = normWeight(p.yarn_weight);
      if (p.total_yardage !== undefined) clean.total_yardage = nn(p.total_yardage);
      if (p.needle_size !== undefined) clean.needle_size = str(p.needle_size);
      if (p.status !== undefined) {
        const cur = db.get(PATTERNS, String(id)) as { status?: string } | null;
        clean.status = normStatus(p.status, cur?.status ?? "queued");
      }
      if (p.source !== undefined) clean.source = str(p.source);
      if (p.notes !== undefined) clean.notes = str(p.notes);
      db.update(PATTERNS, String(id), clean);
      return null;
    },
    delete_pattern: ({ id }) => {
      db.remove(PATTERNS, String(id));
      return null;
    },

    // ── Projects ── (native ORDER BY finished_on-empty ASC, finished_on DESC, created_at DESC)
    list_projects: () =>
      db.all(PROJECTS).sort((a, b) => {
        const fa = String((a as { finished_on?: string }).finished_on ?? "");
        const fb = String((b as { finished_on?: string }).finished_on ?? "");
        const ea = fa === "" ? 1 : 0;
        const eb = fb === "" ? 1 : 0;
        if (ea !== eb) return ea - eb; // non-empty finished_on first
        if (fa !== fb) return fb.localeCompare(fa); // finished_on DESC
        return byCreatedDesc(a, b);
      }),
    get_project: ({ id }) => db.get(PROJECTS, String(id)),
    create_project: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        name: str(i.name),
        yarn_weight: normWeight(i.yarn_weight),
        yardage_used: nn(i.yardage_used),
        finished_on: str(i.finished_on),
        notes: str(i.notes),
        created_at: now,
        updated_at: now,
      };
      return db.insert(PROJECTS, row);
    },
    update_project: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const clean: Record<string, unknown> = { updated_at: nowIso() };
      if (p.name !== undefined) clean.name = str(p.name);
      if (p.yarn_weight !== undefined) clean.yarn_weight = normWeight(p.yarn_weight);
      if (p.yardage_used !== undefined) clean.yardage_used = nn(p.yardage_used);
      if (p.finished_on !== undefined) clean.finished_on = str(p.finished_on);
      if (p.notes !== undefined) clean.notes = str(p.notes);
      db.update(PROJECTS, String(id), clean);
      return null;
    },
    delete_project: ({ id }) => {
      db.remove(PROJECTS, String(id));
      return null;
    },

    // ── Row counters ─────────────────────────────────────────────────────────────
    list_counters: () => {
      const rows = db.all("counters");
      return rows.sort((a, b) => String((a as { created_at?: string }).created_at ?? "").localeCompare(String((b as { created_at?: string }).created_at ?? "")));
    },
    create_counter: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const name = String(i.name ?? "").trim() || "Counter";
      const stepN = Math.round(Number(i.step));
      const step = Number.isFinite(stepN) && stepN !== 0 ? stepN : 1;
      return db.insert("counters", { id: uuid(), name, count: 0, step, created_at: now, updated_at: now });
    },
    set_counter_count: ({ id, count }) => {
      const c = Math.max(0, Math.round(Number(count) || 0));
      db.update("counters", String(id), { count: c, updated_at: nowIso() });
      return null;
    },
    update_counter: ({ id, name, step }) => {
      const next: Record<string, unknown> = { updated_at: nowIso() };
      if (typeof name === "string" && name.trim()) next.name = name.trim();
      const s = Math.round(Number(step));
      if (Number.isFinite(s) && s !== 0) next.step = s;
      db.update("counters", String(id), next);
      return null;
    },
    delete_counter: ({ id }) => {
      db.remove("counters", String(id));
      return null;
    },

    // Derived — 1:1 JS port of signals.rs (signals-web.ts), fence verbatim.
    compute_signals: ({ today: forDate }) => {
      const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
      return computeSignalsWeb(
        db.all(SKEINS) as unknown as Skein[],
        db.all(PATTERNS) as unknown as Pattern[],
        db.all(PROJECTS) as unknown as Project[],
        /^\d{4}-\d{2}-\d{2}/.test(String(forDate ?? "")) ? String(forDate).slice(0, 10) : localToday(),
      );
    },
  };
}
